// askSmart-ui/src/pages/TOSP/portal/delegatesApi.ts
//
// The people a tenant has asked to act for them.
//
// Read and revoke go to TOSP, because the grant lives on the tenancy. The
// invite goes to the shared invite router, which is where every "bring somebody
// into this org for a reason" already lives — one place that knows how to tell
// a new person from an existing member.

import { TenancyDelegate } from "../types";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export type DelegateResult =
  /** They already had an account here — the grant is live now. */
  | { status: "added" }
  /** An invitation is on its way; the grant lands when they register. */
  | { status: "invited" }
  | { status: "error"; message: string };

export async function fetchDelegates(tenantId: string): Promise<TenancyDelegate[]> {
  const res = await fetch(
    `${BACKEND}/tosp/delegates/${encodeURIComponent(tenantId)}`,
    { credentials: "include" },
  );
  if (!res.ok) throw new Error(`Couldn't load the delegates (${res.status}).`);
  const body = await res.json();
  return Array.isArray(body.delegates) ? body.delegates : [];
}

/**
 * Every tenancy in ONE request.
 *
 * This used to be called once per tenancy in a loop. For somebody who already
 * had an account that worked; for anybody else the first call created the
 * invitation and the second was refused as a duplicate, so the invitee
 * registered attached to a single tenancy and the rest were lost. A person is
 * invited once, so they are asked for once.
 */
export async function inviteDelegate(
  tenantIds: string[],
  email: string,
): Promise<DelegateResult> {
  const res = await fetch(`${BACKEND}/invites/delegate`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ tenantIds, email }),
  });

  const body = await res.json().catch(() => ({}));
  if (!res.ok) {
    return {
      status: "error",
      message: body?.message || `The invitation didn't send (${res.status}).`,
    };
  }
  // The outcomes read differently to the person who asked for them: "added" is
  // finished, the others are waiting on somebody else to register. "merged"
  // means they already had an invitation and these tenancies joined it — no
  // second email, because the one they hold already carries the token.
  if (body?.mode === "direct") return { status: "added" };
  return { status: "invited" };
}

export async function revokeDelegate(
  tenantId: string,
  userId: string,
): Promise<void> {
  const res = await fetch(
    `${BACKEND}/tosp/delegates/${encodeURIComponent(tenantId)}/${encodeURIComponent(userId)}`,
    { method: "DELETE", credentials: "include" },
  );
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body?.error || `Couldn't remove them (${res.status}).`);
  }
}
